import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * Speed Test Metrics View - Visualização de métricas de teste de velocidade
 *
 * Exibe tabela comparativa entre velocidades no mesmo padrão visual do
 * HeadlineMetricsOverview: filtro de período (PeriodPicker) + botão
 * "Atualizar" e tabela detalhada com linha "vencedora" destacada.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PeriodPicker } from '@/components/ui/period-picker';
import { BarChart3, Info, Loader2, Star } from 'lucide-react';
import { subDays } from 'date-fns';
import { useSpeedTestMetrics } from '@/hooks/useSpeedTest';
import { cn } from '@/lib/utils';
import { PitchRetentionCell } from '@/components/metrics/pitch/PitchRetentionCell';
import type { TestPitchMetric } from '@/types/pitch';
import { PitchSelector } from '@/components/metrics/pitch/PitchSelector';
import { byPitchOptions, usePitchSelection } from '@/components/metrics/pitch/pitchSelection';

interface SpeedTestMetricsViewProps {
  speedTestId: string;
}

interface SpeedMetricRow {
  variationName: string;
  engajamento: number;
  visualizacoes: number;
  visualizacoesUnicas: number;
  plays: number;
  playsUnicos: number;
  playRate: number;
  cliquesNoBotao: number;
  /** Pitches do vídeo ORIGINAL com a retenção desta velocidade em cada um. */
  pitches: TestPitchMetric[];
  pitchVideoId: string | null;
  videoDuration: number | null;
  isChampion: boolean;
}

const TableHeadWithTooltip = ({
  children,
  tooltip,
}: {
  children: React.ReactNode;
  tooltip: string;
}) => { useLanguage(); return ((
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger className="flex items-center gap-1 cursor-help">
        {children}
        <Info className="h-3 w-3 text-[#9CA3AF]" />
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-xs text-xs">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)); };

const toNumber = (value: any): number => {
  // Number.isFinite descarta Infinity/-Infinity/NaN — nenhuma métrica deve
  // chegar à UI como esses valores (ex.: engajamento calculado com plays=0)
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const SpeedTestMetricsView: React.FC<SpeedTestMetricsViewProps> = ({
  speedTestId,
}) => {
  useLanguage();
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());

  const { data, isLoading, isError, error, refetch } = useSpeedTestMetrics(
    {
      speedTestId,
      inicio: startDate.toISOString(),
      fim: endDate.toISOString(),
    },
    !!speedTestId,
  );

  // Mapeia o retorno do backend para a estrutura da tabela.
  // Backend: { data: [{ speedTest: {...}, metric: { totais: {...} } }] }
  const metrics = useMemo<SpeedMetricRow[]>(() => {
    const raw: any[] = data?.data || [];
    if (raw.length === 0) return [];

    const mapped = raw
      .map((item) => {
        const speedTest = item.speedTest || {};
        const totais = item.metric?.totais || {};
        const speed = toNumber(speedTest.speed) || 1.0;
        return {
          variationName: `Velocidade ${speed}x`,
          engajamento: toNumber(totais.engajamento),
          visualizacoes: toNumber(totais.visualizacoes),
          visualizacoesUnicas: toNumber(totais.visualizacoesUnicas),
          plays: toNumber(totais.plays),
          playsUnicos: toNumber(totais.playsUnicos),
          playRate: toNumber(totais.taxareproducao),
          cliquesNoBotao: toNumber(totais.clicks),
          // Raiz do retorno de MetricService.execute. Os pitches vêm do vídeo
          // ORIGINAL (o clone não herda o campo) — o backend resolve isso via
          // pitchesOverride, e pitchVideoId aponta para o original.
          pitches: Array.isArray(item.metric?.pitches) ? item.metric.pitches : [],
          pitchVideoId: item.metric?.pitchVideoId ?? null,
          videoDuration: item.metric?.videoDuration ?? null,
          isChampion: false,
        };
      })
      .sort((a, b) => b.playRate - a.playRate);

    if (mapped.length > 0) mapped[0].isChampion = true;
    return mapped;
  }, [data]);

  // Seletor de pitch (só aparece com 2+ pitches). As variações compartilham os
  // pitches do vídeo original, então as opções saem da primeira linha.
  const pitchOptions = byPitchOptions(metrics[0]?.pitches ?? []);
  const [pitchId, setPitchId] = usePitchSelection(pitchOptions);

  if (!speedTestId) {
    return (
      <Card className="border-[#E6E6E6]">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground text-center">
            {translateInterfaceText("Selecione um teste para visualizar as métricas.")}</p>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="border-[#E6E6E6]">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-destructive text-center">
              {translateInterfaceText("Erro ao carregar métricas:")}{" "}{error?.message}
            </p>
            <Button onClick={() => refetch()} variant="outline" size="sm">
              {translateInterfaceText("Tentar novamente")}</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtro de Data — mesmo padrão do HeadlineMetricsOverview */}
      <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <PeriodPicker
              value={{ from: startDate, to: endDate }}
              onChange={({ from, to }) => {
                setStartDate(from);
                setEndDate(to);
              }}
              className="md:min-w-[240px]"
            />

            <Button
              onClick={() => refetch()}
              disabled={isLoading}
              size="sm"
              className="ml-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {translateInterfaceText("Carregando...")}</>
              ) : (
                translateInterfaceText("Atualizar")
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Estado de Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">{translateInterfaceText("Carregando métricas...")}</span>
        </div>
      )}

      {/* Mensagem quando não há métricas */}
      {!isLoading && metrics.length === 0 && (
        <div className="text-center py-12">
          <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{translateInterfaceText("Nenhuma métrica disponível")}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {translateInterfaceText("Não há métricas para o período selecionado. Aguarde a coleta de dados ou ajuste o período.")}</p>
        </div>
      )}

      {/* Tabela Detalhada — mesmo padrão do HeadlineMetricsOverview */}
      {!isLoading && metrics.length > 0 && (
        <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
          <CardHeader className="bg-[#F7F9FC] border-b border-[#E6E6E6] py-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-sm font-semibold text-[#1C1C1E]">
                {translateInterfaceText("Comparativo por Velocidade")}</CardTitle>
              <PitchSelector options={pitchOptions} value={pitchId} onChange={setPitchId} />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F7F9FC] hover:bg-[#F7F9FC]">
                    <TableHead className="text-xs font-semibold text-[#6F6F75] whitespace-nowrap">
                      {translateInterfaceText("Velocidade")}</TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual médio de retenção do vídeo assistido")}>
                        {translateInterfaceText("Engajamento")}</TableHeadWithTooltip>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      <TableHeadWithTooltip tooltip={translateInterfaceText("Total de visualizações do vídeo")}>
                        {translateInterfaceText("Visualizações")}</TableHeadWithTooltip>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      <TableHeadWithTooltip tooltip={translateInterfaceText("Visualizações únicas (por usuário)")}>
                        {translateInterfaceText("Vis. Únicas")}</TableHeadWithTooltip>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      {translateInterfaceText("Plays")}</TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      <TableHeadWithTooltip tooltip={translateInterfaceText("Plays únicos por usuário")}>
                        {translateInterfaceText("Plays Únicos")}</TableHeadWithTooltip>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual de visitantes que iniciaram o vídeo")}>
                        {translateInterfaceText("Play Rate")}</TableHeadWithTooltip>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual de espectadores que ainda assistia no momento do pitch")}>
                        {translateInterfaceText("Retenção no Pitch")}</TableHeadWithTooltip>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                      {translateInterfaceText("C. no Botão")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.map((metric, index) => (
                    <TableRow
                      key={index}
                      className={cn(
                        metric.isChampion
                          ? 'bg-[#F0F7FF] border-l-2 border-l-[#1F6BFF] hover:bg-[#E8F4FF]'
                          : index % 2 === 0
                          ? 'bg-white hover:bg-[#F9FAFB]'
                          : 'bg-[#FAFBFC] hover:bg-[#F9FAFB]',
                      )}
                    >
                      <TableCell className="font-medium text-[#1C1C1E]">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[150px]">{metric.variationName}</span>
                          {metric.isChampion && (
                            <Star className="h-3 w-3 text-[#1F6BFF] fill-[#1F6BFF] flex-shrink-0" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E]">
                        {metric.engajamento.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E]">
                        {metric.visualizacoes}
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E]">
                        {metric.visualizacoesUnicas}
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E]">{metric.plays}</TableCell>
                      <TableCell className="text-right text-[#1C1C1E]">
                        {metric.playsUnicos}
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                        {metric.playRate.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                        <PitchRetentionCell
                          pitch={metric.pitches.find((p) => p.id === pitchId) ?? null}
                          videoPitches={metric.pitches}
                          pitchVideoId={metric.pitchVideoId}
                          durationInSeconds={metric.videoDuration}
                          onSaved={() => refetch()}
                        />
                      </TableCell>
                      <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                        {metric.cliquesNoBotao}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SpeedTestMetricsView;
